import { SITE_CONFIG } from '../config/site';
import { skills } from '../data/skills';
import { experience } from '../data/experience';
import { flagshipProject, featuredProjects, metrics } from '../data/projects';
import { getPdfThemeTokens } from './pdfThemeTokens';
import type { GenerateResumePdfOptions, PdfThemeTokens, ExperienceItem } from '../types';

interface RenderState {
  currentY: number;
  currentPage: number;
  readonly pageHeight: number;
  readonly pageWidth: number;
  readonly topMargin: number;
  readonly bottomMargin: number;
  readonly leftMargin: number;
  readonly rightMargin: number;
  readonly printableWidth: number;
  readonly tokens: PdfThemeTokens;
}

function fillCanvasBackground(doc: any, state: RenderState): void {
  doc.setFillColor(...state.tokens.canvasBg);
  doc.rect(0, 0, state.pageWidth, state.pageHeight, 'F');
}

function checkPageBreak(
  doc: any,
  state: RenderState,
  requiredHeightMm: number,
  lookaheadPaddingMm = 0
): boolean {
  const maxY = state.pageHeight - state.bottomMargin;
  if (state.currentY + requiredHeightMm + lookaheadPaddingMm > maxY) {
    doc.addPage();
    state.currentPage += 1;
    fillCanvasBackground(doc, state);
    state.currentY = state.topMargin;
    return true;
  }
  return false;
}

function renderHyperlink(
  doc: any,
  x: number,
  y: number,
  text: string,
  url: string,
  tokens: PdfThemeTokens,
  options: {
    fontSize?: number;
    fontStyle?: 'normal' | 'bold';
    underline?: boolean;
  } = {}
): number {
  const fontSize = options.fontSize ?? 8.5;
  const fontStyle = options.fontStyle ?? 'normal';
  const showUnderline = options.underline ?? true;

  doc.setFont('helvetica', fontStyle);
  doc.setFontSize(fontSize);
  doc.setTextColor(...tokens.linkText);

  doc.text(text, x, y, { baseline: 'top' });

  const textWidth = doc.getTextWidth(text);
  const textHeight = fontSize * 0.352778;

  if (showUnderline) {
    doc.setDrawColor(...tokens.linkUnderline);
    doc.setLineWidth(0.18);
    doc.line(x, y + textHeight + 0.3, x + textWidth, y + textHeight + 0.3);
  }

  const hitPadding = 0.4;
  doc.link(
    x - hitPadding,
    y - hitPadding,
    textWidth + hitPadding * 2,
    textHeight + hitPadding * 2,
    { url }
  );

  return textWidth;
}

function renderSectionTitle(
  doc: any,
  state: RenderState,
  title: string,
  x = state.leftMargin,
  width = state.printableWidth
): void {
  checkPageBreak(doc, state, 10, 8);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...state.tokens.accentTealText);
  doc.text(title.toUpperCase(), x, state.currentY, { baseline: 'top' });

  const titleHeight = 11 * 0.352778;
  const lineY = state.currentY + titleHeight + 1.2;

  doc.setDrawColor(...state.tokens.border);
  doc.setLineWidth(0.25);
  doc.line(x, lineY, x + width, lineY);

  state.currentY = lineY + 3.0;
}

function renderBulletItem(
  doc: any,
  state: RenderState,
  text: string,
  x = state.leftMargin,
  maxWidth = state.printableWidth
): void {
  const indent = 3.5;
  const textW = maxWidth - indent;
  const linePitch = 3.8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const lines: string[] = doc.splitTextToSize(text, textW);
  const totalH = lines.length * linePitch + 1.0;

  checkPageBreak(doc, state, totalH);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...state.tokens.accentAmber);
  doc.text('•', x, state.currentY, { baseline: 'top' });

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...state.tokens.textMuted);
  for (let i = 0; i < lines.length; i++) {
    doc.text(lines[i], x + indent, state.currentY + i * linePitch, { baseline: 'top' });
  }

  state.currentY += totalH;
}

function renderGlobalFooters(doc: any, state: RenderState): void {
  const totalPages = doc.getNumberOfPages();
  const footerY = state.pageHeight - (state.bottomMargin / 2);

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...state.tokens.textFaint);

    const leftText = 'Nagendra Vikram Dixit · Verified Professional Engineering Résumé';
    doc.text(leftText, state.leftMargin, footerY, { baseline: 'bottom' });

    const rightText = `Page ${i} of ${totalPages}`;
    const rightW = doc.getTextWidth(rightText);
    doc.text(rightText, state.pageWidth - state.rightMargin - rightW, footerY, { baseline: 'bottom' });
  }
}

// -------------------------------------------------------------
// PORTRAIT GENERATOR (A4: 210 x 297 mm)
// -------------------------------------------------------------
function generatePortraitDocument(doc: any, tokens: PdfThemeTokens): void {
  const pageWidth = 210;
  const pageHeight = 297;
  const leftMargin = 14;
  const rightMargin = 14;
  const topMargin = 14;
  const bottomMargin = 14;
  const printableWidth = pageWidth - leftMargin - rightMargin;

  const state: RenderState = {
    currentY: topMargin,
    currentPage: 1,
    pageHeight,
    pageWidth,
    topMargin,
    bottomMargin,
    leftMargin,
    rightMargin,
    printableWidth,
    tokens
  };

  fillCanvasBackground(doc, state);

  // 1. Executive Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...tokens.textPrimary);
  doc.text(SITE_CONFIG.name.toUpperCase(), state.leftMargin, state.currentY, { baseline: 'top' });
  state.currentY += 7.0;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...tokens.accentAmberText);
  doc.text(SITE_CONFIG.role, state.leftMargin, state.currentY, { baseline: 'top' });
  state.currentY += 4.5;

  // Contact / Links line
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...tokens.textMuted);
  let curX = state.leftMargin;

  doc.text(`${SITE_CONFIG.location}  |  `, curX, state.currentY, { baseline: 'top' });
  curX += doc.getTextWidth(`${SITE_CONFIG.location}  |  `);

  curX += renderHyperlink(doc, curX, state.currentY, 'nvdixit.co.uk', 'https://nvdixit.co.uk', tokens, { fontSize: 8.5 });
  doc.setTextColor(...tokens.textMuted);
  doc.text('  |  ', curX, state.currentY, { baseline: 'top' });
  curX += doc.getTextWidth('  |  ');

  curX += renderHyperlink(doc, curX, state.currentY, 'linkedin.com/in/nvdixit', 'https://www.linkedin.com/in/nvdixit/', tokens, { fontSize: 8.5 });
  doc.setTextColor(...tokens.textMuted);
  doc.text('  |  ', curX, state.currentY, { baseline: 'top' });
  curX += doc.getTextWidth('  |  ');

  renderHyperlink(doc, curX, state.currentY, 'github.com/nagendradixit647', SITE_CONFIG.githubUrl, tokens, { fontSize: 8.5 });
  state.currentY += 6.5;

  // 2. Executive Professional Summary
  renderSectionTitle(doc, state, 'Executive Professional Summary');
  const summaryText =
    'Senior Backend & AI Systems Engineer with continuous enterprise delivery at Cognizant for tier-1 UK retail infrastructure. Architect of governed, multi-agent AI workflows (LangGraph, pgvector RAG, OpenAI APIs) and high-throughput backend microservices (Python, Java 21, AWS, Kubernetes). Reclaimed 240+ engineering hours annually (240+ hrs/yr) through automation, accelerated IAM provisioning by 99.8%, and enforced zero-mutation AST safety guardrails across 400+ database tables.';
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...tokens.textMuted);
  const summaryLines = doc.splitTextToSize(summaryText, printableWidth);
  for (let i = 0; i < summaryLines.length; i++) {
    doc.text(summaryLines[i], state.leftMargin, state.currentY + i * 3.8, { baseline: 'top' });
  }
  state.currentY += summaryLines.length * 3.8 + 4.0;

  // 3. Technical Skills Matrix
  renderSectionTitle(doc, state, 'Core Technical Competencies');
  for (const cat of skills) {
    checkPageBreak(doc, state, 10);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...tokens.textPrimary);
    const catLabel = `${cat.title}: `;
    doc.text(catLabel, state.leftMargin, state.currentY, { baseline: 'top' });

    const labelW = doc.getTextWidth(catLabel);
    const chipsStr = cat.chips.join(', ');

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...tokens.textMuted);
    const lines = doc.splitTextToSize(chipsStr, printableWidth - labelW);

    doc.text(lines[0], state.leftMargin + labelW, state.currentY, { baseline: 'top' });
    for (let i = 1; i < lines.length; i++) {
      doc.text(lines[i], state.leftMargin + labelW, state.currentY + i * 3.6, { baseline: 'top' });
    }
    state.currentY += Math.max(lines.length * 3.6, 4.2) + 1.2;
  }
  state.currentY += 2.0;

  // 4. Flagship AI Architecture Showcase
  renderSectionTitle(doc, state, 'Flagship Architecture: Enterprise NL-to-SQL Agent (Solo POC)');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...tokens.accentAmberText);
  doc.text('5-Stage Governed Pipeline Translating Plain English to Production-Safe SQL · 2026', state.leftMargin, state.currentY, { baseline: 'top' });
  state.currentY += 4.5;

  renderBulletItem(
    doc,
    state,
    'Architected a 5-stage multi-agent pipeline using LangGraph, pgvector, and FastAPI to safely query 400+ enterprise retail database tables without manual SQL.'
  );
  renderBulletItem(
    doc,
    state,
    'Overcame LLM context window limits via pgvector semantic RAG, dynamically injecting relevant table DDLs with zero code modifications on schema migrations.'
  );
  renderBulletItem(
    doc,
    state,
    'Enforced zero destructive write operations (INSERT/UPDATE/DELETE/DROP) using a deterministic AST validator with strict SELECT-only guardrails.'
  );
  renderBulletItem(
    doc,
    state,
    'Applied Keycloak RBAC domain rules paired with Human-in-the-Loop (HITL) approval gates for queries accessing sensitive customer or payment data.'
  );
  state.currentY += 2.0;

  // 5. Professional Experience - Current Role
  renderSectionTitle(doc, state, 'Professional Experience');
  const role1 = experience[0];
  if (role1) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...tokens.textPrimary);
    doc.text(role1.org.toUpperCase(), state.leftMargin, state.currentY, { baseline: 'top' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...tokens.textFaint);
    const locStr = role1.location;
    doc.text(locStr, state.pageWidth - state.rightMargin - doc.getTextWidth(locStr), state.currentY, { baseline: 'top' });
    state.currentY += 4.2;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...tokens.accentTealText);
    doc.text(role1.title, state.leftMargin, state.currentY, { baseline: 'top' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...tokens.textFaint);
    const dateStr = 'Nov 2025 – Present';
    doc.text(dateStr, state.pageWidth - state.rightMargin - doc.getTextWidth(dateStr), state.currentY, { baseline: 'top' });
    state.currentY += 4.2;

    for (const bullet of role1.bullets) {
      renderBulletItem(doc, state, bullet);
    }
  }

  // Force clean break to Page 2 for past roles and project depth
  if (state.currentPage === 1) {
    doc.addPage();
    state.currentPage = 2;
    fillCanvasBackground(doc, state);
    state.currentY = state.topMargin;
  }

  // Running Mini-Header on Page 2
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...tokens.textFaint);
  doc.text('NAGENDRA VIKRAM DIXIT · CURRICULUM VITAE (CONTINUED)', state.leftMargin, state.currentY, { baseline: 'top' });
  state.currentY += 5.0;

  // Roles 2 & 3
  const pastRoles: ExperienceItem[] = experience.slice(1);
  for (const role of pastRoles) {
    checkPageBreak(doc, state, 20, 10);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...tokens.textPrimary);
    doc.text(role.org.toUpperCase(), state.leftMargin, state.currentY, { baseline: 'top' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...tokens.textFaint);
    const locStr = role.location;
    doc.text(locStr, state.pageWidth - state.rightMargin - doc.getTextWidth(locStr), state.currentY, { baseline: 'top' });
    state.currentY += 4.2;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...tokens.accentTealText);
    doc.text(role.title, state.leftMargin, state.currentY, { baseline: 'top' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...tokens.textFaint);
    const dateStr = role.start.startsWith('2024') ? 'Jan 2024 – Nov 2025' : 'Nov 2020 – Jan 2024';
    doc.text(dateStr, state.pageWidth - state.rightMargin - doc.getTextWidth(dateStr), state.currentY, { baseline: 'top' });
    state.currentY += 4.2;

    for (const bullet of role.bullets.slice(0, 5)) {
      renderBulletItem(doc, state, bullet);
    }
    state.currentY += 2.0;
  }

  // Key Technical Projects & Frameworks
  renderSectionTitle(doc, state, 'Key Production Repositories & Open Source Builds');
  for (const proj of featuredProjects) {
    checkPageBreak(doc, state, 16);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...tokens.textPrimary);
    doc.text(proj.title, state.leftMargin, state.currentY, { baseline: 'top' });

    if (proj.githubUrl) {
      const urlX = state.leftMargin + doc.getTextWidth(proj.title) + 4.0;
      renderHyperlink(doc, urlX, state.currentY, '[GitHub Repository]', proj.githubUrl, tokens, { fontSize: 8 });
    }
    state.currentY += 4.0;

    renderBulletItem(doc, state, proj.desc);
    state.currentY += 1.0;
  }

  // Quantified Impact Metrics Ledger
  renderSectionTitle(doc, state, 'Verified Impact & Automation Ledger');
  for (const m of metrics) {
    checkPageBreak(doc, state, 9);
    const metricSummary = `${m.num} ${m.unit} (${m.impact}): ${m.title} — ${m.desc}`;
    renderBulletItem(doc, state, metricSummary);
  }

  // Education & Academic Credentials
  renderSectionTitle(doc, state, 'Education & Academic Credentials');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...tokens.textPrimary);
  doc.text('Bachelor of Technology (B.Tech.) in Information Technology', state.leftMargin, state.currentY, { baseline: 'top' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...tokens.textFaint);
  doc.text('2020', state.pageWidth - state.rightMargin - doc.getTextWidth('2020'), state.currentY, { baseline: 'top' });
  state.currentY += 4.0;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...tokens.textMuted);
  doc.text('Dr. A.P.J. Abdul Kalam Technical University (AKTU), India', state.leftMargin, state.currentY, { baseline: 'top' });
  state.currentY += 6.0;

  renderGlobalFooters(doc, state);
}

// -------------------------------------------------------------
// LANDSCAPE GENERATOR (A4: 297 x 210 mm Widescreen Overview)
// -------------------------------------------------------------
function generateLandscapeDocument(doc: any, tokens: PdfThemeTokens): void {
  const pageWidth = 297;
  const pageHeight = 210;
  const leftMargin = 14;
  const rightMargin = 14;
  const topMargin = 12;
  const bottomMargin = 12;
  const printableWidth = pageWidth - leftMargin - rightMargin;

  const sidebarW = 76;
  const gap = 8;
  const mainColX = leftMargin + sidebarW + gap;
  const mainColW = printableWidth - sidebarW - gap;

  const state: RenderState = {
    currentY: topMargin,
    currentPage: 1,
    pageHeight,
    pageWidth,
    topMargin,
    bottomMargin,
    leftMargin,
    rightMargin,
    printableWidth,
    tokens
  };

  fillCanvasBackground(doc, state);

  // Top Banner across full width
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...tokens.textPrimary);
  const nameText = SITE_CONFIG.name.toUpperCase();
  const nameWidth = doc.getTextWidth(nameText);
  doc.text(nameText, state.leftMargin, state.currentY, { baseline: 'top' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...tokens.accentAmberText);
  doc.text(` · ${SITE_CONFIG.role}`, state.leftMargin + nameWidth + 2.0, state.currentY + 1.8, { baseline: 'top' });
  state.currentY += 6.5;

  // Contact line
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...tokens.textMuted);
  let banX = state.leftMargin;
  doc.text(`${SITE_CONFIG.location}  |  `, banX, state.currentY, { baseline: 'top' });
  banX += doc.getTextWidth(`${SITE_CONFIG.location}  |  `);
  banX += renderHyperlink(doc, banX, state.currentY, 'nvdixit.co.uk', 'https://nvdixit.co.uk', tokens, { fontSize: 8 });
  doc.setTextColor(...tokens.textMuted);
  doc.text('  |  ', banX, state.currentY, { baseline: 'top' });
  banX += doc.getTextWidth('  |  ');
  banX += renderHyperlink(doc, banX, state.currentY, 'linkedin.com/in/nvdixit', 'https://www.linkedin.com/in/nvdixit/', tokens, { fontSize: 8 });
  doc.setTextColor(...tokens.textMuted);
  doc.text('  |  ', banX, state.currentY, { baseline: 'top' });
  banX += doc.getTextWidth('  |  ');
  renderHyperlink(doc, banX, state.currentY, 'github.com/nagendradixit647', SITE_CONFIG.githubUrl, tokens, { fontSize: 8 });

  state.currentY += 5.5;

  // Divider line
  doc.setDrawColor(...tokens.border);
  doc.setLineWidth(0.25);
  doc.line(state.leftMargin, state.currentY, state.pageWidth - state.rightMargin, state.currentY);
  state.currentY += 3.5;

  const contentStartY = state.currentY;

  // -----------------------------------------------------------
  // LEFT COLUMN: Sidebar (Profile, Competencies, ROI Metrics)
  // -----------------------------------------------------------
  let sideY = contentStartY;

  // Sidebar container rect
  doc.setFillColor(...tokens.panelBg);
  doc.setDrawColor(...tokens.border);
  doc.setLineWidth(0.2);
  doc.roundedRect(state.leftMargin, sideY, sidebarW, 168, 2.0, 2.0, 'FD');

  const sidePad = 4.0;
  const sideContentX = state.leftMargin + sidePad;
  const sideContentW = sidebarW - sidePad * 2;
  sideY += sidePad;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...tokens.accentTealText);
  doc.text('EXECUTIVE PROFILE', sideContentX, sideY, { baseline: 'top' });
  sideY += 4.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...tokens.textMuted);
  const profileLines = doc.splitTextToSize(
    'Nearly 6 years engineering high-scale backend microservices and governed multi-agent AI pipelines at Cognizant for UK retail infrastructure. Expert in LangGraph, Java 21, Python, AWS, and Kubernetes.',
    sideContentW
  );
  for (const line of profileLines) {
    doc.text(line, sideContentX, sideY, { baseline: 'top' });
    sideY += 3.2;
  }
  sideY += 3.0;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...tokens.accentTealText);
  doc.text('CORE COMPETENCIES', sideContentX, sideY, { baseline: 'top' });
  sideY += 4.5;

  for (const cat of skills) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...tokens.textPrimary);
    doc.text(cat.title, sideContentX, sideY, { baseline: 'top' });
    sideY += 3.2;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...tokens.textMuted);
    const chipsStr = cat.chips.slice(0, 6).join(' · ');
    const chipLines = doc.splitTextToSize(chipsStr, sideContentW);
    for (const cline of chipLines) {
      doc.text(cline, sideContentX, sideY, { baseline: 'top' });
      sideY += 2.9;
    }
    sideY += 1.5;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...tokens.accentTealText);
  doc.text('VERIFIED ROI METRICS', sideContentX, sideY, { baseline: 'top' });
  sideY += 4.2;

  for (const m of metrics) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...tokens.accentAmberText);
    doc.text(`• ${m.num} ${m.unit}`, sideContentX, sideY, { baseline: 'top' });
    sideY += 3.0;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...tokens.textMuted);
    const mLines = doc.splitTextToSize(m.impact, sideContentW);
    for (const mline of mLines) {
      doc.text(mline, sideContentX + 3.0, sideY, { baseline: 'top' });
      sideY += 2.8;
    }
    sideY += 1.0;
  }

  // -----------------------------------------------------------
  // RIGHT COLUMN: Flagship Architecture & Experience Progression
  // -----------------------------------------------------------
  let mainY = contentStartY;

  // Flagship Project Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...tokens.textPrimary);
  doc.text('FLAGSHIP: GOVERNED NL-TO-SQL AI AGENT (SOLO POC)', mainColX, mainY, { baseline: 'top' });
  mainY += 4.0;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...tokens.accentAmberText);
  doc.text('LangGraph · pgvector RAG · SELECT-Only AST Validator · Keycloak RBAC · 400+ Tables', mainColX, mainY, { baseline: 'top' });
  mainY += 4.0;

  // 5 Pipeline Stage Cards
  const stageW = (mainColW - 8) / 5;
  const stageH = 18;
  const stages = flagshipProject.pipelineStages;

  for (let i = 0; i < stages.length; i++) {
    const stage = stages[i];
    const sx = mainColX + i * (stageW + 2);

    doc.setFillColor(...tokens.panelBg);
    doc.setDrawColor(...tokens.border);
    doc.setLineWidth(0.2);
    doc.roundedRect(sx, mainY, stageW, stageH, 1.5, 1.5, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...tokens.accentTealText);
    doc.text(stage.step, sx + 2, mainY + 2.5, { baseline: 'top' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...tokens.textPrimary);
    const titleLines = doc.splitTextToSize(stage.title, stageW - 4);
    for (let t = 0; t < Math.min(titleLines.length, 2); t++) {
      doc.text(titleLines[t], sx + 2, mainY + 6.0 + t * 2.8, { baseline: 'top' });
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...tokens.textFaint);
    const descLines = doc.splitTextToSize(stage.shortDesc, stageW - 4);
    if (descLines.length > 0) {
      doc.text(descLines[0], sx + 2, mainY + 12.5, { baseline: 'top' });
    }
  }
  mainY += stageH + 5.0;

  // Professional Experience Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...tokens.textPrimary);
  doc.text('PROFESSIONAL EXPERIENCE (COGNIZANT · UK RETAIL CLIENT)', mainColX, mainY, { baseline: 'top' });
  mainY += 4.5;

  for (const expItem of experience) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...tokens.accentTealText);
    doc.text(expItem.title, mainColX, mainY, { baseline: 'top' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...tokens.textFaint);
    const dateLoc = `${expItem.location} | ${expItem.start.slice(0, 7)} to ${expItem.end ? expItem.end.slice(0, 7) : 'Present'}`;
    doc.text(dateLoc, state.pageWidth - state.rightMargin - doc.getTextWidth(dateLoc), mainY, { baseline: 'top' });
    mainY += 3.5;

    for (const bullet of expItem.bullets.slice(0, 2)) {
      const bLines: string[] = doc.splitTextToSize(bullet, mainColW - 4);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...tokens.accentAmber);
      doc.text('•', mainColX, mainY, { baseline: 'top' });

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...tokens.textMuted);
      for (const bl of bLines) {
        doc.text(bl, mainColX + 3.5, mainY, { baseline: 'top' });
        mainY += 3.2;
      }
    }
    mainY += 1.5;
  }

  // Featured Repositories
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...tokens.textPrimary);
  doc.text('FEATURED REPOSITORIES', mainColX, mainY, { baseline: 'top' });
  mainY += 4.0;

  for (const proj of featuredProjects.slice(0, 2)) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...tokens.textPrimary);
    doc.text(proj.title, mainColX, mainY, { baseline: 'top' });

    if (proj.githubUrl) {
      renderHyperlink(doc, mainColX + doc.getTextWidth(proj.title) + 4, mainY, '[GitHub]', proj.githubUrl, tokens, { fontSize: 7.5 });
    }
    mainY += 3.2;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...tokens.textMuted);
    const pLines: string[] = doc.splitTextToSize(proj.tagline, mainColW - 4);
    for (const pline of pLines.slice(0, 2)) {
      doc.text(pline, mainColX + 3.5, mainY, { baseline: 'top' });
      mainY += 3.0;
    }
    mainY += 1.5;
  }

  renderGlobalFooters(doc, state);
}

// -------------------------------------------------------------
// PUBLIC DISPATCH ENGINE
// -------------------------------------------------------------
export async function buildResumePdfDocument(options: GenerateResumePdfOptions): Promise<any> {
  const { jsPDF } = await import('jspdf');
  const tokens = getPdfThemeTokens(options.theme || 'dark');
  const orientation = options.orientation || 'portrait';

  const doc = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  if (orientation === 'landscape') {
    generateLandscapeDocument(doc, tokens);
  } else {
    generatePortraitDocument(doc, tokens);
  }

  return doc;
}

export async function generateResumePdf(options: GenerateResumePdfOptions): Promise<void> {
  const doc = await buildResumePdfDocument(options);
  const orientation = options.orientation || 'portrait';
  const filename = `Nagendra_Dixit_Resume_${orientation}_${options.theme || 'dark'}.pdf`;
  doc.save(filename);
}

export default generateResumePdf;

